import { useState, useCallback, useEffect, useRef } from 'react'
import { PanelLeft, Plus, Pencil } from 'lucide-react'
import { ZinsserChat } from './ZinsserChat'
import { ZinsserDocumentPanel } from './ZinsserDocumentPanel'
import { ZinsserSessionSidebar } from './ZinsserSessionSidebar'
import { ZinsserRenameSessionModal } from './ZinsserRenameSessionModal'
import { ZinsserMobileNav } from './ZinsserMobileNav'
import { ResizableDivider } from '@/features/develop/components/ResizableDivider'
import { useZinsserStore } from '@/stores/useZinsserStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'

const MIN_PANEL_RATIO = 0.25  // Minimum 25% of available width
const MAX_PANEL_RATIO = 0.65  // Maximum 65% of available width
const DEFAULT_PANEL_RATIO = 0.5  // Default 50% split
const SIDEBAR_WIDTH = 260

// Calculate padding based on viewport width
const getPadding = () => {
  if (typeof window === 'undefined') return 24
  return window.innerWidth >= 1536 ? 128 : window.innerWidth >= 1280 ? 80 : window.innerWidth >= 1024 ? 48 : 24
}

// Calculate available width for panels (excluding sidebar and padding)
const getAvailableWidth = (hasSidebar: boolean) => {
  if (typeof window === 'undefined') return 1000
  const padding = getPadding()
  return window.innerWidth - (padding * 2) - (hasSidebar ? SIDEBAR_WIDTH : 0)
}

// Storage key for tracking which user's data is in localStorage
const ZINSSER_USER_ID_KEY = 'zinsser-store-user-id'

export function ZinsserView() {
  const {
    sessionName,
    mobileView,
    showSessionSidebar,
    toggleSessionSidebar,
    closeSessionSidebar,
    createNewSession,
    loadSessionFromDb,
    renameSession,
    setMobileView,
    startNewSession,
  } = useZinsserStore()

  const isMobile = useIsMobile()
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [isVerifyingUser, setIsVerifyingUser] = useState(true)  // Block UI until user verified

  // Get auth state - need to wait for auth initialization before creating sessions
  const { initialized: authInitialized, session: authSession, initialize: initializeAuth } = useAuthStore()

  const hasInitialized = useRef(false)

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // CRITICAL: Verify user ownership BEFORE showing any content
  // This runs early and blocks UI rendering until verification completes
  useEffect(() => {
    if (!authInitialized) return
    if (!authSession?.user?.id) {
      // Not logged in - redirect will happen in the other effect
      setIsVerifyingUser(false)
      return
    }

    const currentUserId = authSession.user.id
    const storedUserId = localStorage.getItem(ZINSSER_USER_ID_KEY)

    // If stored data belongs to different user, clear it IMMEDIATELY
    if (storedUserId && storedUserId !== currentUserId) {
      // Store new user ID FIRST
      localStorage.setItem(ZINSSER_USER_ID_KEY, currentUserId)

      // Clear localStorage zinsser-store-v3
      localStorage.removeItem('zinsser-store-v3')

      // Also use Zustand's persist API to clear storage if available
      // This ensures the persist middleware doesn't write stale data back
      const persistApi = (useZinsserStore as unknown as { persist?: { clearStorage: () => void } }).persist
      if (persistApi?.clearStorage) {
        persistApi.clearStorage()
      }

      // Reset in-memory store - this clears all session data
      startNewSession()

      // Force a rehydration from the now-empty localStorage
      const rehydrate = (useZinsserStore as unknown as { persist?: { rehydrate: () => Promise<void> } }).persist?.rehydrate
      if (rehydrate) {
        rehydrate()
      }

      // Reset initialization so we create a new session
      hasInitialized.current = false
    } else if (!storedUserId) {
      // First time for this user
      localStorage.setItem(ZINSSER_USER_ID_KEY, currentUserId)
    }

    // User verified, allow rendering
    setIsVerifyingUser(false)
  }, [authInitialized, authSession?.user?.id, startNewSession])

  // On mount: wait for auth + user verification, then reload session from DB or create new
  useEffect(() => {
    // Prevent double initialization
    if (hasInitialized.current) return

    // Wait for auth to be initialized
    if (!authInitialized) {
      console.log('[ZinsserView] Waiting for auth initialization...')
      return
    }

    // Wait for user verification to complete
    if (isVerifyingUser) {
      console.log('[ZinsserView] Waiting for user verification...')
      return
    }

    // Check if user is authenticated
    if (!authSession) {
      console.log('[ZinsserView] No auth session, redirecting to login')
      window.location.href = '/login?returnTo=' + encodeURIComponent(window.location.pathname)
      return
    }

    // Check if Zustand has finished hydrating from localStorage
    const checkAndInit = async () => {
      // Small delay to ensure Zustand hydration completes
      await new Promise(resolve => setTimeout(resolve, 50))

      // Get fresh state after hydration
      const state = useZinsserStore.getState()
      const hydratedSessionId = state.sessionId

      if (hasInitialized.current) return  // Double-check after await
      hasInitialized.current = true

      if (hydratedSessionId) {
        console.log('[ZinsserView] Loading session from DB:', hydratedSessionId)
        loadSessionFromDb(hydratedSessionId)
      } else {
        console.log('[ZinsserView] No session found, creating new')
        createNewSession()
      }
    }

    checkAndInit()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInitialized, authSession, isVerifyingUser]) // Re-run when auth state changes

  // Store panel ratio (0-1) instead of fixed pixel width
  // This maintains aspect ratio when window is resized
  const [documentRatio, setDocumentRatio] = useState(DEFAULT_PANEL_RATIO)

  // Track window width to trigger re-render on resize
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  )

  // Listen for window resize to maintain panel ratios
  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [])

  // Calculate actual pixel width from ratio (recalculates when windowWidth or sidebar changes)
  const availableWidth = getAvailableWidth(showSessionSidebar)
  const documentWidth = Math.floor(availableWidth * documentRatio)

  // Suppress unused variable warning - windowWidth triggers re-render for availableWidth calculation
  void windowWidth

  // Handle divider drag - convert pixel delta to ratio change
  const handleResize = useCallback((delta: number) => {
    setDocumentRatio(prev => {
      const currentAvailable = getAvailableWidth(showSessionSidebar)
      // Delta is positive when moving right (shrinking panel), negative when moving left (growing panel)
      const ratioChange = delta / currentAvailable
      const newRatio = prev - ratioChange
      return Math.min(MAX_PANEL_RATIO, Math.max(MIN_PANEL_RATIO, newRatio))
    })
  }, [showSessionSidebar])

  // Handle session selection on mobile - switch to chat view
  const handleMobileSessionSelect = useCallback(() => {
    setMobileView('chat')
  }, [setMobileView])

  // CRITICAL: Block rendering until user verification completes
  // This prevents showing another user's data even briefly
  if (isVerifyingUser || !authInitialized) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    )
  }

  // Mobile layout
  if (isMobile) {
    return (
      <div className="h-dvh flex flex-col bg-background overflow-hidden">
        {/* Mobile Header */}
        <header className="flex-shrink-0 h-14 border-b border-border">
          <div className="px-4 h-full flex items-center justify-between">
            {/* Left side: Session name */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <h1 className="text-sm font-medium text-foreground truncate">{sessionName}</h1>
              <button
                onClick={() => setShowRenameModal(true)}
                className="p-1.5 text-muted-foreground hover:text-foreground rounded transition-colors flex-shrink-0"
                title="Rename session"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>

            {/* Right side: New session button */}
            <button
              onClick={() => createNewSession()}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title="New session"
            >
              <Plus className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </header>

        {/* Main content area - single view at a time */}
        {/* pb-14 = 56px for nav height, additional safe-area handled by nav itself */}
        <main className="flex-1 overflow-hidden pb-14 pb-mobile-nav">
          {mobileView === 'sessions' && (
            <div className="h-full">
              <ZinsserSessionSidebar
                onClose={() => setMobileView('chat')}
                onSessionSelect={handleMobileSessionSelect}
              />
            </div>
          )}
          {mobileView === 'chat' && (
            <div className="h-full">
              <ZinsserChat />
            </div>
          )}
          {mobileView === 'document' && (
            <div className="h-full">
              <ZinsserDocumentPanel />
            </div>
          )}
        </main>

        {/* Mobile bottom navigation */}
        <ZinsserMobileNav />

        {/* Rename Session Modal */}
        <ZinsserRenameSessionModal
          isOpen={showRenameModal}
          currentName={sessionName}
          onClose={() => setShowRenameModal(false)}
          onSubmit={(name) => {
            renameSession(name)
            setShowRenameModal(false)
          }}
        />
      </div>
    )
  }

  // Desktop layout (existing)
  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 h-14">
        <div className="px-6 lg:px-12 xl:px-20 2xl:px-32 h-full flex items-center justify-between">
          {/* Left side: Toggle and New Session icons (replacing "Card Studio") */}
          <div className="flex items-center gap-1">
            <button
              onClick={toggleSessionSidebar}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title={showSessionSidebar ? "Hide sessions" : "Show sessions"}
            >
              <PanelLeft className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => createNewSession()}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
              title="New session"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Main content area */}
      <main className="flex-1 overflow-hidden">
        <div className="h-full px-6 lg:px-12 xl:px-20 2xl:px-32 flex">
          {/* Session Sidebar - inside the constrained width */}
          {showSessionSidebar && (
            <div
              className="flex-shrink-0 h-full border-r border-border"
              style={{ width: SIDEBAR_WIDTH }}
            >
              <ZinsserSessionSidebar onClose={closeSessionSidebar} />
            </div>
          )}

          {/* Main content column - title, chat */}
          <div className={cn("flex-1 flex flex-col min-w-0", showSessionSidebar && "pl-4")}>
            {/* Session title - above chat */}
            <div className="flex-shrink-0 pt-3 pb-2 flex items-center gap-2 group">
              <h1 className="text-sm font-medium text-foreground">{sessionName}</h1>
              <button
                onClick={() => setShowRenameModal(true)}
                className="p-1 text-muted-foreground/0 group-hover:text-muted-foreground hover:!text-foreground rounded transition-colors"
                title="Rename session"
              >
                <Pencil className="w-3 h-3" strokeWidth={1.5} />
              </button>
            </div>

            {/* Chat pane */}
            <div className="flex-1 overflow-hidden">
              <ZinsserChat />
            </div>
          </div>

          <ResizableDivider onResize={handleResize} />

          {/* Document pane */}
          <div
            className="flex-shrink-0 overflow-hidden"
            style={{ width: documentWidth }}
          >
            <ZinsserDocumentPanel />
          </div>
        </div>
      </main>

      {/* Rename Session Modal */}
      <ZinsserRenameSessionModal
        isOpen={showRenameModal}
        currentName={sessionName}
        onClose={() => setShowRenameModal(false)}
        onSubmit={(name) => {
          renameSession(name)
          setShowRenameModal(false)
        }}
      />
    </div>
  )
}
